/*
 * MENTIONS PATTERN SOURCED FROM ORIGINAL IMPLEMENTATION IN BACKBONE
 * Original @mentions pattern written in mentions.patterns.js.coffee.
 * Methods used to write mentionable comments to the database have been duplicated and commented out
 * Parts of the pattern that are not implemented in the original were not duplicated.
 */

/*
 * Formatting Pattern.
 * This is used when you need to match against either the username or the ID of
 * mention in question.
 *
 * @example
 *   "@[ZÅk Remer](user:12452)".match(formattingPattern)
 *   # => ["@[ZÅk Remer](user:12452)"], "ZÅk Remer", "12452"]
 */
export const formattingPattern = new RegExp(
  '@\\[([^\\]]+)\\]\\(\\w+:(\\d+)\\)',
  'gi'
)

/*
 * Parse Mention String
 * This function takes a opal formatted mention and then both retreives and
 * formats the information into a hash.
 *
 * @example
 *   parseMentionString("@[ZÅk Remer](user:1234)")
 *   # => { name: "@ZÅk Remer", id: 1234 }
 */
export const parseMentionString = (string) => {
  const name = string.split('[')[1].split(']')[0]
  const id = parseInt(string.split('user:')[1].split(')')[0], 10)
  return {
    name,
    id,
  }
}

/*
 * Determines all the mentioned users within a comment and returns an array
 * containing information about those mentioned users.
 *
 * @method getMentionedUsers
 * @param string   {String}   The string of which to parse out mentions from.
 * @return {Array}   An array of user objects for every mention in the passed in text.
 */
export const getMentionedUsers = (string) => {
  const mentions = string.match(formattingPattern)
  if (!mentions || !mentions.length) return []

  return mentions.map((mention) => parseMentionString(mention))
}

/*
 * Linkify Mention Object
 * This function takes a mention object and
 * formats the information into a JSX link.
 *
 * @example
 *   linkifyMentionObject({ name: "@ZÅk Remer", id: 1234 })
 *   # => <a ... href='/people/1234'>@ZÅk Remer</a>
 */
export const linkifyMentionObject = (mentionObj, idx = 0) => (
  <a
    key={`${mentionObj.id}-${idx}`}
    className={`fn url user_${mentionObj.id} fn custom_handler`}
    data-globalid={`user_${mentionObj.id}`}
    data-id={`${mentionObj.id}`}
    href={`/people/${mentionObj.id}`}
  >
    {mentionObj.name}
  </a>
)

/*
 * Prepare mention text for render in React component
 * This function prepares a string for render -
 *
 * @example
 *   formatMentions("Dan Barrett (REAL)")
 *   # => "Dan Barrett \(REAL\)"
 */

const formatMentions = (comment) => {
  if (!comment) return null
  // return comment without @ mentions in format that works for parser
  const users = getMentionedUsers(comment)
  if (!users.length) return [<span key={0}>{comment}</span>]

  // otherwise replace mentions with mention JSX...
  const commentSections = comment.split(formattingPattern)
  users.forEach((user, idx) => {
    const mentionJSX = linkifyMentionObject(user, idx)
    const userPosition = () =>
      commentSections.findIndex((string) => string === user.name)
    return commentSections.splice(userPosition(), 2, mentionJSX)
  })
  // ...and other strings with spans
  const outputArray = commentSections.map((section, idx) => {
    if (typeof section === 'object') return section
    return <span key={idx}>{section}</span>
  })

  return outputArray
}

/* USED TO SAVE COMMENTS AFTER INPUT - NOT YET IMPLEMENTED IN REACT */

/*
 * Typing Mentions Pattern
 * This pattern is used to match against when a user is typing an @mention into
 * an input and it has not been added as a complete @mention.
 *
 * @example
 *   "blah blah @ZÅk Remer".match(typingPattern)
 *   # => ["@ZÅk"], "ZÅk"]
 */

// const typingPattern = new RegExp('\\B@([^\\s]{3,})');

/*
 * Clean String for Regexp
 * This function prepares a string for insertion into a Regexp - it escapes
 * reserved characters so things like () will not be interpreted as regex control
 * characters
 *
 * @example
 *   regexCleanString("Dan Barrett (REAL)")
 *   # => "Dan Barrett \(REAL\)"
 */
// const regexCleanString = string => string.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');

export default formatMentions
